import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { useI18n } from 'next-localization';
import useWindowDimensions from 'hooks/useWindowDimensions';
import useHelpers from 'hooks/helpers';
import { standardFormat } from '@/constants/dateFormats';
import CustomScroll from 'components/Functional/CustomScroll';
import { PerformanceSet, TabData } from '@/store/fundDetails/typings';

type Props = {
  tabData: TabData;
  footnoteText: string;
  wrapperId: string;
  tableId: string;
};

const FundDetailsGeneralPerformanceTable = ({
  tabData,
  tableId,
  wrapperId,
  footnoteText,
}: Props) => {
  const [gradientPosition, setGradientPosition] = useState('gradient-right');
  const { deviceType } = useWindowDimensions();
  const { localizeDate, formatNumber } = useHelpers();
  const { t } = useI18n();

  const isScrollable = useMemo(() => {
    if (tabData === null || tabData.performanceSet.length < 1) return false;
    if (deviceType === 'mobile') {
      return tabData.performanceSet[0].values.length >= 2;
    } else if (deviceType === 'tablet') {
      return tabData.performanceSet[0].values.length > 4;
    } else {
      return tabData.performanceSet[0].values.length > 6;
    }
  }, [deviceType, tabData]);

  const availableColumns = useMemo(() => {
    const columns = [];
    tabData?.performanceSet.map((set) => {
      set.values.map((value) => {
        if (
          !columns.filter((item) => item.performanceTimePeriod === value.performanceTimePeriod)
            .length
        ) {
          columns.push({
            performanceTimePeriod: value.performanceTimePeriod.toLowerCase(),
            performanceTimePeriodLabel: value.performanceTimePeriodLabel,
          });
        }
      });
    });
    return columns;
  }, [tabData]);

  const possibleColumns = [
    '1 month',
    '3 months',
    '6 months',
    '1 year',
    '3 years',
    '5 years',
    '10 years',
    'launch',
    'year to date',
  ];

  const footnote = footnoteText + ' ' + localizeDate(tabData?.lastRelevantDate, standardFormat);

  const haveColumn = (colName: string) => {
    return !!availableColumns.filter((col) => col.performanceTimePeriod === colName).length;
  };

  const getLabel = (colName: string) => {
    return availableColumns.find((col) => col.performanceTimePeriod === colName)
      .performanceTimePeriodLabel;
  };

  const cellValue = (cell: PerformanceSet, colName: string) => {
    const findValue = cell.values.find(
      (value) => value.performanceTimePeriod.toLowerCase() === colName
    );
    return findValue ? formatNumber(findValue.value) : t('notAvailableShortMessage');
  };

  return (
    <div>
      <div className="position-relative">
        <div
          id={wrapperId}
          className={classNames('abrdn-table', {
            scrollable: isScrollable,
            [gradientPosition]: gradientPosition,
          })}
        >
          <table
            id="tableId"
            aria-label="Fund details performace alt tab table"
            className="not-striped performance-table"
          >
            <thead>
              <tr>
                <th scope="col" className="sticky-column" />
                {possibleColumns.map(
                  (colName) =>
                    haveColumn(colName) && (
                      <th key={colName} scope="col">
                        {t(getLabel(colName))}
                      </th>
                    )
                )}
              </tr>
            </thead>
            <tbody>
              {tabData.performanceSet.map((item) => (
                <tr key={item.performanceLabel}>
                  <th scope="row" className="sticky-column">
                    {item.performanceLabel}
                  </th>
                  {possibleColumns.map(
                    (colName) =>
                      haveColumn(colName) && (
                        <td key={colName} scope="col">
                          {cellValue(item, colName)}
                        </td>
                      )
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-1 footnote text-grey">{footnote}</div>

      {isScrollable && (
        <CustomScroll
          deviceType={deviceType}
          wrapperId={wrapperId}
          tableId={tableId}
          changeGradientPosition={setGradientPosition}
        />
      )}
    </div>
  );
};

export default FundDetailsGeneralPerformanceTable;
